import type { Client } from '@urql/core'
import { runInAction } from 'mobx'
import { graphql } from 'app/generated/gql'

import type { Appointment as AppointmentType, AppointmentListResult } from 'app/generated/graphql'
import { EntityStore, type ListParams } from './EntityStore'

/** The index returns at most this many rows a page, whatever limit is asked for. */
const READ_ALL_PAGE = 1000
/** A ceiling on pages, so a runaway range degrades to a partial answer rather than an open-ended read. */
const READ_ALL_MAX_PAGES = 20

const APPOINTMENT_FIELDS = `amount appointmentMadeDate confirmed createdDate date description duration id locationId note patientId pmsStatus procedureCode1 procedureCode1Amount procedureCode1Time procedureCode2 procedureCode2Amount procedureCode2Time procedureCode3 procedureCode3Amount procedureCode3Time procedureCodes providerId sikkaOfficeId sikkaPracticeId source status time timeToAppointment type updatedDate version`

const LIST_QUERY = graphql(`
  query Appointments($search: String, $pagination: PaginationInput, $sort: SortInput) {
    appointments(search: $search, pagination: $pagination, sort: $sort) {
      hits {
        amount
        appointmentMadeDate
        confirmed
        createdDate
        date
        description
        duration
        id
        locationId
        note
        patientId
        pmsStatus
        procedureCode1
        procedureCode1Amount
        procedureCode1Time
        procedureCode2
        procedureCode2Amount
        procedureCode2Time
        procedureCode3
        procedureCode3Amount
        procedureCode3Time
        procedureCodes
        providerId
        sikkaOfficeId
        sikkaPracticeId
        source
        status
        time
        timeToAppointment
        type
        updatedDate
        version
      }
      total
      page
    }
  }
`)

const GET_QUERY = graphql(`
  query Appointment($id: ID!) {
    appointment(id: $id) {
      amount
      appointmentMadeDate
      confirmed
      createdDate
      date
      description
      duration
      id
      locationId
      note
      patientId
      pmsStatus
      procedureCode1
      procedureCode1Amount
      procedureCode1Time
      procedureCode2
      procedureCode2Amount
      procedureCode2Time
      procedureCode3
      procedureCode3Amount
      procedureCode3Time
      procedureCodes
      providerId
      sikkaOfficeId
      sikkaPracticeId
      source
      status
      time
      timeToAppointment
      type
      updatedDate
      version
    }
  }
`)

export class AppointmentStore extends EntityStore<AppointmentType, AppointmentListResult> {
  constructor(client: Client) {
    super(client, {
      listQuery: LIST_QUERY,
      getQuery: GET_QUERY,
      extractList: (data: any) => data.appointments,
      extractOne: (data: any) => data.appointment,
      extractMutated: (data: any) => data.updateAppointmentType,
    })
  }

  /**
   * Every appointment a search matches, page by page, as the shared instances. Fresh from the
   * server, and `list` is left alone, so a calendar read never replaces the Appointments table.
   */
  async readAll(params: Omit<ListParams, `pagination`>): Promise<AppointmentType[]> {
    const rows: AppointmentType[] = []
    for (let page = 0; page < READ_ALL_MAX_PAGES; page++) {
      const result = await this.listSource(
        { ...params, pagination: { page, limit: READ_ALL_PAGE } },
        `network-only`,
      ).toPromise()
      if (result.error) throw new Error(result.error.message)
      const list = this.config.extractList(result.data)
      rows.push(...runInAction(() => this.internList(list.hits)))
      if (list.hits.length < READ_ALL_PAGE || rows.length >= list.total) break
    }
    return rows
  }
}
